/**
 * Global Error Context
 * Provides centralized error state management and error handling functions
 * 
 * Features:
 * - Centralized error state management
 * - Network error handling with retry mechanisms
 * - Application error handling with user-friendly messages
 * - Error recovery and cleanup functions
 * - Integration with error boundary and notification systems
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { notification } from 'antd';

// Error types
export const ERROR_TYPES = {
  NETWORK: 'NETWORK',
  APPLICATION: 'APPLICATION',
  VALIDATION: 'VALIDATION',
  AUTHENTICATION: 'AUTHENTICATION',
  AUTHORIZATION: 'AUTHORIZATION',
  NOT_FOUND: 'NOT_FOUND',
  SERVER: 'SERVER',
  UNKNOWN: 'UNKNOWN',
};

// Error severity levels
export const ERROR_SEVERITY = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL',
};

// Initial state
const initialState = {
  errors: [],
  currentError: null,
  isErrorModalOpen: false,
  networkStatus: 'online',
  retryCount: 0,
  maxRetries: 3,
};

// Action types
const ACTION_TYPES = {
  ADD_ERROR: 'ADD_ERROR',
  REMOVE_ERROR: 'REMOVE_ERROR',
  CLEAR_ERRORS: 'CLEAR_ERRORS',
  SET_CURRENT_ERROR: 'SET_CURRENT_ERROR',
  TOGGLE_ERROR_MODAL: 'TOGGLE_ERROR_MODAL',
  SET_NETWORK_STATUS: 'SET_NETWORK_STATUS',
  INCREMENT_RETRY_COUNT: 'INCREMENT_RETRY_COUNT',
  RESET_RETRY_COUNT: 'RESET_RETRY_COUNT',
};

// Reducer
const errorReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.ADD_ERROR:
      return {
        ...state,
        errors: [...state.errors, action.payload],
        currentError: action.payload,
        isErrorModalOpen: action.payload.showModal || false,
      };
    case ACTION_TYPES.REMOVE_ERROR:
      return {
        ...state,
        errors: state.errors.filter(error => error.id !== action.payload),
        currentError: state.currentError?.id === action.payload ? null : state.currentError,
      };
    case ACTION_TYPES.CLEAR_ERRORS:
      return {
        ...state,
        errors: [],
        currentError: null,
        isErrorModalOpen: false,
      };
    case ACTION_TYPES.SET_CURRENT_ERROR:
      return {
        ...state,
        currentError: action.payload,
        isErrorModalOpen: !!action.payload,
      };
    case ACTION_TYPES.TOGGLE_ERROR_MODAL:
      return {
        ...state,
        isErrorModalOpen: action.payload,
      };
    case ACTION_TYPES.SET_NETWORK_STATUS:
      return {
        ...state,
        networkStatus: action.payload,
      };
    case ACTION_TYPES.INCREMENT_RETRY_COUNT:
      return {
        ...state,
        retryCount: state.retryCount + 1,
      };
    case ACTION_TYPES.RESET_RETRY_COUNT:
      return {
        ...state,
        retryCount: 0,
      };
    default:
      return state;
  }
};

// Create context
const ErrorContext = createContext();

// Custom hook to use error context
export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

// Error provider component
export const ErrorProvider = ({ children }) => {
  const [state, dispatch] = useReducer(errorReducer, initialState);

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => {
      dispatch({ type: ACTION_TYPES.SET_NETWORK_STATUS, payload: 'online' });
      dispatch({ type: ACTION_TYPES.RESET_RETRY_COUNT });
      notification.success({
        message: 'You\'re Back Online! 🎉',
        description: 'Your internet connection has been restored and you can continue using the app.',
        duration: 3,
      });
    };

    const handleOffline = () => {
      dispatch({ type: ACTION_TYPES.SET_NETWORK_STATUS, payload: 'offline' });
      notification.warning({
        message: 'No Internet Connection',
        description: 'You\'re currently offline. Some features may not work until your connection is restored.',
        duration: 0, // Don't auto-close
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Generate unique error ID
  const generateErrorId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  };

  // Get user-friendly error message
  const getUserFriendlyMessage = (error, type) => {
    switch (type) {
      case ERROR_TYPES.NETWORK:
        if (state.networkStatus === 'offline') {
          return 'It looks like you\'re not connected to the internet. Please check your connection and try again.';
        }
        if (error?.code === 'ECONNREFUSED' || error?.message?.includes('Network Error')) {
          return 'We\'re having trouble connecting to our services right now. Please wait a moment and try again.';
        }
        if (error?.response?.status === 404) {
          return 'We couldn\'t find what you\'re looking for. Please double-check the information you entered.';
        }
        if (error?.response?.status >= 500) {
          return 'Our system is experiencing some technical difficulties. We\'re working to fix this as quickly as possible.';
        }
        if (error?.response?.status === 429) {
          return 'You\'ve made quite a few requests recently. Please wait a moment before trying again.';
        }
        return 'We\'re experiencing some connection issues. Please check your internet and try again.';
      
      case ERROR_TYPES.AUTHENTICATION:
        return 'Your session has expired for security reasons. Please refresh the page to continue.';
      
      case ERROR_TYPES.AUTHORIZATION:
        return 'You don\'t have permission to access this information. Please contact support if you think this is a mistake.';
      
      case ERROR_TYPES.VALIDATION:
        return error?.message || 'Please check the information you entered and make sure everything is filled out correctly.';
      
      case ERROR_TYPES.APPLICATION:
        if (error?.message?.includes('ChunkLoadError')) {
          return 'We\'ve updated the application. Please refresh your page to get the latest version.';
        }
        return 'Something unexpected happened. Don\'t worry - your information is safe. Please try again.';
      
      case ERROR_TYPES.SERVER:
        return 'Our servers are taking a little break. Please try again in a few minutes.';
      
      default:
        return 'Oops! Something didn\'t work as expected. Please try again, and if the problem continues, let us know.';
    }
  };

  // Get error severity based on type and details
  const getErrorSeverity = (error, type) => {
    switch (type) {
      case ERROR_TYPES.NETWORK:
        if (state.networkStatus === 'offline') return ERROR_SEVERITY.HIGH;
        if (error?.response?.status >= 500) return ERROR_SEVERITY.HIGH;
        if (error?.response?.status === 404) return ERROR_SEVERITY.MEDIUM;
        return ERROR_SEVERITY.MEDIUM;
      
      case ERROR_TYPES.APPLICATION:
        if (error?.message?.includes('ChunkLoadError')) return ERROR_SEVERITY.HIGH;
        return ERROR_SEVERITY.MEDIUM;
      
      case ERROR_TYPES.AUTHENTICATION:
      case ERROR_TYPES.AUTHORIZATION:
        return ERROR_SEVERITY.HIGH;
      
      case ERROR_TYPES.VALIDATION:
        return ERROR_SEVERITY.LOW;
      
      case ERROR_TYPES.SERVER:
        return ERROR_SEVERITY.CRITICAL;
      
      default:
        return ERROR_SEVERITY.MEDIUM;
    }
  };

  // Add error function
  const addError = useCallback(async (error, type = ERROR_TYPES.UNKNOWN, options = {}) => {
    const errorId = generateErrorId();
    const severity = getErrorSeverity(error, type);
    const userMessage = getUserFriendlyMessage(error, type);

    const errorObject = {
      id: errorId,
      type,
      severity,
      originalError: error,
      message: userMessage,
      timestamp: new Date().toISOString(),
      showModal: options.showModal !== false, // Default to true
      showNotification: options.showNotification !== false, // Default to true
      retryable: options.retryable || type === ERROR_TYPES.NETWORK,
      onRetry: options.onRetry,
      metadata: {
        url: window.location.href,
        userAgent: navigator.userAgent,
        ...options.metadata,
      },
    };

    dispatch({ type: ACTION_TYPES.ADD_ERROR, payload: errorObject });

    // Show notification if enabled
    if (errorObject.showNotification) {
      const notificationType = severity === ERROR_SEVERITY.CRITICAL ? 'error' : 
                              severity === ERROR_SEVERITY.HIGH ? 'error' : 
                              severity === ERROR_SEVERITY.MEDIUM ? 'warning' : 'info';
      
      notification[notificationType]({
        message: severity === ERROR_SEVERITY.CRITICAL ? 'Urgent Issue' :
                severity === ERROR_SEVERITY.HIGH ? 'Something Needs Attention' :
                severity === ERROR_SEVERITY.MEDIUM ? 'Heads Up' :
                'Just So You Know',
        description: userMessage,
        duration: severity === ERROR_SEVERITY.CRITICAL ? 0 : 4.5,
      });
    }

    // Log error for debugging
    console.error(`[ErrorContext] ${type} Error:`, error);
    console.error(`[ErrorContext] Error Details:`, errorObject);

    return errorId;
  }, [state.networkStatus]);

  // Remove error function
  const removeError = useCallback((errorId) => {
    dispatch({ type: ACTION_TYPES.REMOVE_ERROR, payload: errorId });
  }, []);

  // Clear all errors function
  const clearErrors = useCallback(() => {
    dispatch({ type: ACTION_TYPES.CLEAR_ERRORS });
  }, []);

  // Set current error function
  const setCurrentError = useCallback((error) => {
    dispatch({ type: ACTION_TYPES.SET_CURRENT_ERROR, payload: error });
  }, []);

  // Toggle error modal function
  const toggleErrorModal = useCallback((isOpen) => {
    dispatch({ type: ACTION_TYPES.TOGGLE_ERROR_MODAL, payload: isOpen });
  }, []);

  // Retry function
  const retryOperation = useCallback(async (errorId) => {
    const error = state.errors.find(e => e.id === errorId);
    if (!error || !error.retryable || !error.onRetry) {
      return false;
    }

    if (state.retryCount >= state.maxRetries) {
      notification.error({
        message: 'We\'ve Tried Our Best',
        description: 'We attempted to fix this several times, but it\'s still not working. Please try again in a few minutes.',
      });
      return false;
    }

    try {
      dispatch({ type: ACTION_TYPES.INCREMENT_RETRY_COUNT });
      await error.onRetry();
      removeError(errorId);
      dispatch({ type: ACTION_TYPES.RESET_RETRY_COUNT });
      
      notification.success({
        message: 'Success! 🎉',
        description: 'Everything is working perfectly now.',
      });
      
      return true;
    } catch (retryError) {
      addError(retryError, ERROR_TYPES.NETWORK, {
        showModal: false,
        retryable: true,
        onRetry: error.onRetry,
      });
      return false;
    }
  }, [state.errors, state.retryCount, state.maxRetries, removeError, addError]);

  // Context value
  const value = {
    // State
    errors: state.errors,
    currentError: state.currentError,
    isErrorModalOpen: state.isErrorModalOpen,
    networkStatus: state.networkStatus,
    retryCount: state.retryCount,
    maxRetries: state.maxRetries,
    
    // Actions
    addError,
    removeError,
    clearErrors,
    setCurrentError,
    toggleErrorModal,
    retryOperation,
    
    // Constants
    ERROR_TYPES,
    ERROR_SEVERITY,
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
    </ErrorContext.Provider>
  );
};

ErrorProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ErrorContext;